'use client';

import { useState } from 'react';
import { Grid3X3, List, Shield, UserPlus, Search } from 'lucide-react';
import { UserCard } from '@/components/user-card';
import { Header } from '@/components/header';
import { users } from '@/lib/data';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { User } from '@/lib/types';

export default function UsersPage() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const allUsers = users;
  const activeUsers = users.filter(u => u.status === 'active');
  const suspendedUsers = users.filter(u => u.status === 'suspended');

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsAddDialogOpen(true);
  };

  const handleView = (user: User) => {
    console.log('View user:', user);
  };

  const handlePermission = (user: User) => {
    setSelectedUser(user);
    setIsPermissionDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Header 
        title="用户管理" 
        subtitle={`共 ${allUsers.length} 位用户`}
        onAdd={() => setIsAddDialogOpen(true)}
      />

      <div className="p-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-[#112240] border border-[#1d3557]">
            <p className="text-sm text-[#8892B0]">用户总数</p>
            <p className="text-2xl font-bold text-[#E6F1FF] mt-1">{allUsers.length}</p>
          </div>
          <div className="p-4 rounded-xl bg-[#112240] border border-[#1d3557]">
            <p className="text-sm text-[#8892B0]">正常用户</p>
            <p className="text-2xl font-bold text-[#10B981] mt-1">{activeUsers.length}</p>
          </div>
          <div className="p-4 rounded-xl bg-[#112240] border border-[#1d3557]">
            <p className="text-sm text-[#8892B0]">暂停账户</p>
            <p className="text-2xl font-bold text-[#EF4444] mt-1">{suspendedUsers.length}</p>
          </div>
          <div className="p-4 rounded-xl bg-[#112240] border border-[#1d3557]">
            <p className="text-sm text-[#8892B0]">本月新增</p>
            <p className="text-2xl font-bold text-[#64FFDA] mt-1">3</p>
          </div>
        </div>

        {/* Tabs and Filters */}
        <Tabs defaultValue="all" className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-[#112240] border border-[#1d3557]">
              <TabsTrigger value="all" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
                全部用户
                <Badge className="ml-2 bg-[#3B82F6]/20 text-[#3B82F6] border-0">{allUsers.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="active" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
                正常
                <Badge className="ml-2 bg-[#10B981]/20 text-[#10B981] border-0">{activeUsers.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="suspended" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
                已暂停
                <Badge className="ml-2 bg-[#EF4444]/20 text-[#EF4444] border-0">{suspendedUsers.length}</Badge>
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-[#112240] rounded-lg p-1 border border-[#1d3557]">
                <Button
                  variant="ghost"
                  size="icon"
                  className={viewMode === 'grid' ? 'bg-[#1d3557] text-[#64FFDA]' : 'text-[#8892B0]'}
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={viewMode === 'list' ? 'bg-[#1d3557] text-[#64FFDA]' : 'text-[#8892B0]'}
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <TabsContent value="all" className={viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-3"
          }>
            {allUsers.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                view={viewMode}
                onEdit={handleEdit}
                onView={handleView}
              />
            ))}
          </TabsContent>

          <TabsContent value="active" className={viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-3"
          }>
            {activeUsers.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                view={viewMode}
                onEdit={handleEdit}
                onView={handleView}
              />
            ))}
          </TabsContent>

          <TabsContent value="suspended" className={viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-3"
          }>
            {suspendedUsers.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                view={viewMode}
                onEdit={handleEdit}
                onView={handleView}
              />
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedUser ? '编辑用户' : '添加用户'}</DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              {selectedUser ? '修改用户信息' : '录入新用户信息'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>姓名</Label>
                <Input 
                  className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                  placeholder="输入姓名"
                  defaultValue={selectedUser?.name}
                />
              </div>
              <div className="space-y-2">
                <Label>手机号</Label>
                <Input 
                  className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                  placeholder="输入手机号"
                  defaultValue={selectedUser?.phone}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>邮箱</Label>
              <Input 
                className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                placeholder="输入邮箱"
                defaultValue={selectedUser?.email}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>角色</Label>
                <Select defaultValue={selectedUser?.role || 'reader'}>
                  <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-[#1d3557]">
                    <SelectItem value="admin">管理员</SelectItem>
                    <SelectItem value="librarian">图书管理员</SelectItem>
                    <SelectItem value="reader">读者</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>最大借阅数</Label>
                <Input 
                  type="number"
                  className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                  placeholder="5"
                  defaultValue={selectedUser?.maxBooks}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>账户有效期至</Label>
              <Input 
                type="date"
                className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                defaultValue={selectedUser?.expiryDate}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsAddDialogOpen(false)}>
              取消
            </Button>
            <Button className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90">
              {selectedUser ? '保存修改' : '添加用户'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog open={isPermissionDialogOpen} onOpenChange={setIsPermissionDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#8B5CF6]" />
              权限管理
            </DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              设置用户 {selectedUser?.name} 的系统权限
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              {[
                { id: 'books', label: '图书管理', desc: '添加、编辑、删除图书' },
                { id: 'borrow', label: '借阅管理', desc: '办理借阅、归还、续借' },
                { id: 'users', label: '用户管理', desc: '管理用户信息' },
                { id: 'categories', label: '分类管理', desc: '管理图书分类' },
                { id: 'statistics', label: '查看统计', desc: '查看系统统计数据' },
                { id: 'settings', label: '系统设置', desc: '修改系统配置' },
              ].map((perm) => (
                <div key={perm.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0A192F] border border-[#1d3557]">
                  <div>
                    <p className="text-sm text-[#E6F1FF]">{perm.label}</p>
                    <p className="text-xs text-[#8892B0]">{perm.desc}</p>
                  </div>
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded border-[#1d3557] bg-[#0A192F] text-[#64FFDA] focus:ring-[#64FFDA]/20"
                    defaultChecked={selectedUser?.role === 'admin'}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsPermissionDialogOpen(false)}>
              取消
            </Button>
            <Button className="bg-[#8B5CF6] text-white hover:bg-[#8B5CF6]/90">
              保存权限
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
